<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps<{
  src: string
  poster?: string
  label: string
}>()

const emit = defineEmits<{
  ready: [video: HTMLVideoElement]
  canplay: [video: HTMLVideoElement]
  error: [video: HTMLVideoElement]
}>()

const host = ref<HTMLElement | null>(null)
let videoElement: HTMLVideoElement | null = null

const staticVideoMarkup = `
  <video
    muted
    autoplay
    playsinline
    webkit-playsinline="true"
    x5-playsinline="true"
    x5-video-player-type="h5-page"
    x5-video-player-fullscreen="false"
    loop
    preload="auto"
    disablepictureinpicture
    class="hero-device__media hero-device__video"
  ></video>
`

const applyTextAttributes = () => {
  if (!videoElement) return
  videoElement.setAttribute('aria-label', props.label)
  if (props.poster) videoElement.setAttribute('poster', props.poster)
  else videoElement.removeAttribute('poster')
}

const applySource = () => {
  if (!videoElement) return
  const current = videoElement.getAttribute('src')
  if (current === props.src) return
  // 早期元素从 CDN 加载失败、已经回退到本服务器的同一个文件（./media/...）：保持回退，不再切回 CDN
  if (current?.startsWith('./media/') && props.src.endsWith(current.slice(1))) return
  // muted/autoplay/playsinline 已由静态 HTML 解析完成，最后才设置动态 src。
  videoElement.setAttribute('src', props.src)
}

const handleCanPlay = () => {
  if (videoElement) emit('canplay', videoElement)
}

const handleError = () => {
  if (videoElement) emit('error', videoElement)
}

onMounted(() => {
  if (!host.value) return

  const earlyVideo = document.querySelector<HTMLVideoElement>('#openrare-early-video')
  if (earlyVideo) {
    earlyVideo.removeAttribute('id')
    earlyVideo.removeAttribute('aria-hidden')
    earlyVideo.removeAttribute('style')
    host.value.appendChild(earlyVideo)
    videoElement = earlyVideo
  } else {
    host.value.innerHTML = staticVideoMarkup
    videoElement = host.value.querySelector('video')
  }
  if (!videoElement) return

  applyTextAttributes()
  applySource()
  videoElement.addEventListener('canplay', handleCanPlay)
  videoElement.addEventListener('error', handleError)
  emit('ready', videoElement)
})

watch(() => props.src, () => {
  applySource()
  videoElement?.load()
})

watch([() => props.poster, () => props.label], applyTextAttributes)

onBeforeUnmount(() => {
  videoElement?.removeEventListener('canplay', handleCanPlay)
  videoElement?.removeEventListener('error', handleError)
  videoElement = null
})
</script>

<template>
  <span ref="host" class="hero-device__video-host"></span>
</template>
